
// Autocomplétion de ville : sur un <input data-city-autocomplete>, interroge le endpoint
// suggestions du géocodage (data-suggestions-url) au fil de la frappe, propose une liste, et au
// choix remplit sur place les champs lat/lon/timezone du formulaire (data-target-lat/-lon/-tz).
// Aucune navigation → tout le reste du formulaire (le mois) est préservé.

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;

// A page can hold more than one of these (a synastry form asks for two cities), and
// `aria-controls` / `aria-activedescendant` point at ids: they have to be unique.
let instances = 0;

function attach(input) {
    const url = input.dataset.suggestionsUrl;
    if (!url) return;

    const listId = `city-autocomplete-list-${++instances}`;

    const form = input.closest('form');
    const container = input.closest('.city-autocomplete') || input.parentElement;
    container.classList.add('city-autocomplete');

    const targetField = (attr) => (form && input.dataset[attr]) ? form.querySelector(`[name="${input.dataset[attr]}"]`) : null;
    const latField = targetField('targetLat');
    const lonField = targetField('targetLon');
    const tzField = targetField('targetTz');

    // THE WIDGET SAYS WHAT IT IS. Without this the list was a plain <ul> of <li>: it worked
    // with a mouse and with the arrow keys, and a screen reader announced nothing at all —
    // neither that suggestions had appeared, nor how many, nor which one the arrows were on.
    // The pattern is the ARIA combobox: the input owns the state, the list is the popup.
    const list = document.createElement('ul');
    list.className = 'city-autocomplete-list';
    list.id = listId;
    list.setAttribute('role', 'listbox');
    container.appendChild(list);

    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);

    let items = [];
    let active = -1;
    let timer = null;

    function close() {
        list.innerHTML = '';
        list.classList.remove('open');
        items = [];
        active = -1;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
    }

    function choose(s) {
        input.value = s.countryCode ? `${s.name}, ${s.countryCode}` : s.name;
        if (latField) latField.value = s.latitude;
        if (lonField) lonField.value = s.longitude;
        if (tzField) tzField.value = s.timezone;
        // Notifier les watchers (ex. désactivation du submit) que ces champs cachés sont remplis :
        // une affectation .value ne déclenche pas d'événement, on émet donc un `change` explicite.
        [latField, lonField, tzField, input].forEach((f) => f && f.dispatchEvent(new Event('change', { bubbles: true })));
        close();
    }

    function highlight() {
        [...list.children].forEach((li, i) => {
            const on = i === active;
            li.classList.toggle('active', on);
            // `aria-selected` and the class say the SAME thing to two different readers; the
            // focus never leaves the input, so what the arrows point at can only be announced
            // through `aria-activedescendant`.
            li.setAttribute('aria-selected', on ? 'true' : 'false');
            if (on) input.setAttribute('aria-activedescendant', li.id);
        });
        if (active < 0) input.removeAttribute('aria-activedescendant');
    }

    function render(suggestions) {
        items = suggestions;
        active = -1;
        list.innerHTML = '';
        if (!suggestions.length) { close(); return; }
        suggestions.forEach((s, i) => {
            const li = document.createElement('li');
            li.className = 'city-autocomplete-item';
            li.id = `${listId}-option-${i}`;
            li.setAttribute('role', 'option');
            li.setAttribute('aria-selected', 'false');
            const label = s.countryCode ? `${s.name}, ${s.countryCode}` : s.name;
            li.textContent = label;
            // mousedown (pas click) pour agir avant le blur de l'input.
            li.addEventListener('mousedown', (e) => { e.preventDefault(); choose(s); });
            list.appendChild(li);
        });
        list.classList.add('open');
        input.setAttribute('aria-expanded', 'true');
    }

    // The kind of place the box offers (data-kind) : cities when it says nothing, `landmark` for what
    // one hopes to see from a window, `any` for a place the way passes through.
    const kind = input.dataset.kind ? `&kind=${encodeURIComponent(input.dataset.kind)}` : '';
    function fetchSuggestions(q) {
        fetch(`${url}?query=${encodeURIComponent(q)}${kind}`, { headers: { Accept: 'application/json' } })
            .then((r) => (r.ok ? r.json() : { suggestions: [] }))
            .then((d) => render(d.suggestions || []))
            .catch(() => close());
    }

    input.setAttribute('autocomplete', 'off');

    input.addEventListener('input', () => {
        const q = input.value.trim();
        clearTimeout(timer);
        if (q.length < MIN_CHARS) { close(); return; }
        timer = setTimeout(() => fetchSuggestions(q), DEBOUNCE_MS);
    });

    input.addEventListener('keydown', (e) => {
        if (!list.classList.contains('open')) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); active = Math.min(active + 1, items.length - 1); highlight(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); active = Math.max(active - 1, 0); highlight(); }
        else if (e.key === 'Enter' && active >= 0) { e.preventDefault(); choose(items[active]); }
        else if (e.key === 'Escape') { close(); }
    });

    // Fermer au blur (léger délai pour laisser le mousedown d'un item agir).
    input.addEventListener('blur', () => setTimeout(close, 150));
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('input[data-city-autocomplete]').forEach(attach);
});
